import React, { useEffect, useRef, useState } from "react";
import { Link2, Loader2, CalendarRange, MapPinned, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";

const fmtDate = (mmddyyyy) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(mmddyyyy || "");
  if (!m) return mmddyyyy || "";
  const d = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

/**
 * URL-mode input for the scan modal: paste a realtor.ca map link, we call
 * /openhouses/parse-search-url (debounced) and show what will be searched.
 * `onPreview(previewOrNull)` lets the parent gate the submit button.
 */
export default function SearchUrlPreview({ url, onUrlChange, maxResults, onPreview }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const trimmed = (url || "").trim();
    if (!trimmed) {
      setPreview(null); setError(""); setLoading(false); onPreview?.(null);
      return undefined;
    }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/openhouses/parse-search-url", {
          search_url: trimmed, max_results: maxResults, oh_days: 4,
        });
        setPreview(data); setError(""); onPreview?.(data);
      } catch (err) {
        setPreview(null); setError(formatApiError(err)); onPreview?.(null);
      } finally {
        setLoading(false);
      }
    }, 450);
    return () => timer.current && clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, maxResults]);

  return (
    <div>
      <Label htmlFor="scan-search-url" className="text-xs uppercase tracking-wide text-slate-600">
        Realtor.ca search link
      </Label>
      <div className="relative mt-1">
        <Link2 className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          id="scan-search-url"
          value={url}
          onChange={(e) => onUrlChange(e.target.value)}
          placeholder="https://www.realtor.ca/map#ZoomLevel=11&LatitudeMax=…"
          autoComplete="off"
          spellCheck={false}
          className="pl-9 min-h-[44px] text-sm font-mono"
          data-testid="scan-search-url-input"
        />
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
        ) : null}
      </div>
      <p className="mt-1 text-[11px] text-slate-500 leading-snug">
        On realtor.ca, run your search in <strong>Map</strong> view with the Open House filter on, then copy the
        address-bar link. Page numbers don&apos;t matter — we pull every page of that search.
      </p>

      {error ? (
        <div
          className="mt-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
          data-testid="scan-url-error"
        >
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      ) : null}

      {preview ? (
        <div
          className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-slate-800 space-y-1"
          data-testid="scan-url-preview"
        >
          <div className="flex items-center gap-1.5 font-semibold text-emerald-900">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Link understood
          </div>
          <div className="flex items-center gap-1.5">
            <MapPinned className="h-3.5 w-3.5 text-slate-500" />
            <span data-testid="scan-url-preview-place">
              {preview.geo_name || "Custom map area"}
            </span>
            <span className="text-slate-500">
              · {preview.box_km?.width} × {preview.box_km?.height} km
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <CalendarRange className="h-3.5 w-3.5 text-slate-500" />
            <span data-testid="scan-url-preview-window">
              Open houses {fmtDate(preview.oh_start)} → {fmtDate(preview.oh_end)}
            </span>
            <span className="text-slate-500">
              {preview.window_source === "url" ? "(from your link)" : "(link had no open-house dates — using today → +4 days)"}
            </span>
          </div>
          {!preview.has_open_house_filter ? (
            <div className="text-amber-800">
              Your link didn&apos;t have the Open House filter on — we add it automatically.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
